import { useState, useEffect, useRef, useCallback } from 'react';
import { Sidebar } from '@/components/Sidebar';
import { Button } from '@/components/ui/button';
import { MessageCircle, Send, ArrowUpRight, XCircle, Sparkles, User, Users, Shield, Stethoscope, ChevronLeft, Bookmark, Plus, Trash2, X, Star, Paperclip, Download, FolderInput, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/api';

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

export default function MessengerPage() {
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [role, setRole] = useState('doctor');
  const [myId, setMyId] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [newTemplate, setNewTemplate] = useState('');
  const [hasDoctors, setHasDoctors] = useState(true);
  const [clinicDoctors, setClinicDoctors] = useState([]);
  const [isClinic, setIsClinic] = useState(false);
  const [escalateDialogOpen, setEscalateDialogOpen] = useState(false);
  const [chatTab, setChatTab] = useState('patients'); // 'patients' | 'team'
  const [staffThreads, setStaffThreads] = useState([]);
  const [staffMessages, setStaffMessages] = useState([]);
  const [selectedStaffThread, setSelectedStaffThread] = useState(null);
  const [staffText, setStaffText] = useState('');
  const [colleagues, setColleagues] = useState([]);
  const [folders, setFolders] = useState([]);
  const [newFolderName, setNewFolderName] = useState('');
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [dragOverFolder, setDragOverFolder] = useState(null);
  const [draggingThread, setDraggingThread] = useState(null);
  const [activeFolderFilter, setActiveFolderFilter] = useState(null); // null=all, ''=unfiled, folder_id=specific
  const [renameFolderId, setRenameFolderId] = useState(null);
  const [renameFolderVal, setRenameFolderVal] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const fetchThreads = useCallback(async () => {
    try {
      const { data } = await api.get('/chat/threads');
      setThreads(Array.isArray(data) ? data : []);
    } catch {}
  }, []);

  const fetchMessages = useCallback(async () => {
    if (!selectedThread) return;
    try {
      const { data } = await api.get(`/chat/threads/${selectedThread.id}/messages`);
      setMessages(Array.isArray(data) ? data : []);
    } catch {}
  }, [selectedThread]);

  const fetchTemplates = useCallback(async () => {
    try { const { data } = await api.get('/chat/templates'); setTemplates(Array.isArray(data) ? data : []); } catch {}
  }, []);

  const fetchStaffThreads = useCallback(async () => {
    try { const { data } = await api.get('/staff-chat/threads'); setStaffThreads(Array.isArray(data) ? data : []); } catch {}
  }, []);

  const fetchStaffMessages = useCallback(async () => {
    if (!selectedStaffThread) return;
    try { const { data } = await api.get(`/staff-chat/threads/${selectedStaffThread.id}/messages`); setStaffMessages(Array.isArray(data) ? data : []); } catch {}
  }, [selectedStaffThread]);

  const fetchColleagues = useCallback(async () => {
    try { const { data } = await api.get('/staff-chat/colleagues'); setColleagues(Array.isArray(data) ? data : []); } catch {}
  }, []);

  const fetchFolders = useCallback(async () => {
    try { const { data } = await api.get('/staff-chat/folders'); setFolders(Array.isArray(data) ? data : []); } catch {}
  }, []);

  useEffect(() => {
    api.get('/auth/me').then(({ data }) => {
      setRole(data.role || 'doctor');
      setMyId(data.psychologist_id || '');
      // Load delegation settings
      api.get('/chat/delegation').then(({ data: del }) => {
        setIsClinic(del.is_clinic || false);
      }).catch(() => {});
      // Load doctors list (for admin escalation)
      if (data.role === 'admin') {
        api.get('/team/doctors').then(({ data: docs }) => {
          const docList = Array.isArray(docs) ? docs : [];
          setClinicDoctors(docList);
          setHasDoctors(docList.length > 0);
        }).catch(() => setHasDoctors(false));
      }
    }).catch(() => {});
    fetchThreads();
    fetchTemplates();
    fetchStaffThreads();
    fetchColleagues();
    fetchFolders();
    const interval = setInterval(() => { fetchThreads(); fetchStaffThreads(); }, 10000);
    return () => clearInterval(interval);
  }, [fetchThreads, fetchTemplates, fetchStaffThreads, fetchColleagues, fetchFolders]);

  useEffect(() => {
    if (selectedThread) {
      fetchMessages();
      const interval = setInterval(fetchMessages, 5000);
      return () => clearInterval(interval);
    }
  }, [selectedThread, fetchMessages]);

  useEffect(() => {
    if (selectedStaffThread) {
      fetchStaffMessages();
      const interval = setInterval(fetchStaffMessages, 5000);
      return () => clearInterval(interval);
    }
  }, [selectedStaffThread, fetchStaffMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    if (!text.trim() || !selectedThread || sending) return;
    setSending(true);
    try {
      await api.post(`/chat/threads/${selectedThread.id}/messages`, {
        text: text.trim(),
        sender_name: role === 'admin' ? 'Администратор' : 'Врач',
      });
      setText('');
      setSuggestions([]);
      fetchMessages();
      fetchThreads();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка отправки');
    }
    setSending(false);
  };

  const sendStaffMessage = async () => {
    if (!staffText.trim() || !selectedStaffThread) return;
    try {
      await api.post(`/staff-chat/threads/${selectedStaffThread.id}/messages`, { text: staffText });
      setStaffText('');
      fetchStaffMessages();
      fetchStaffThreads();
    } catch { toast.error('Ошибка'); }
  };

  const startStaffChat = async (colleagueId) => {
    try {
      const { data } = await api.post(`/staff-chat/threads?target_id=${colleagueId}`);
      setSelectedStaffThread(data);
      fetchStaffThreads();
    } catch (err) { toast.error(err.response?.data?.detail || 'Ошибка'); }
  };

  const createFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      await api.post('/staff-chat/folders', { name: newFolderName });
      setNewFolderName('');
      setShowNewFolder(false);
      fetchFolders();
      toast.success('Папка создана');
    } catch { toast.error('Ошибка'); }
  };

  const deleteFolder = async (folderId) => {
    try {
      await api.delete(`/staff-chat/folders/${folderId}`);
      fetchFolders();
      if (activeFolderFilter === folderId) setActiveFolderFilter(null);
      toast.success('Папка удалена');
    } catch { toast.error('Ошибка'); }
  };

  const renameFolder = async () => {
    if (!renameFolderId || !renameFolderVal.trim()) return;
    try {
      await api.put(`/staff-chat/folders/${renameFolderId}`, { name: renameFolderVal.trim() });
      fetchFolders();
      setRenameFolderId(null);
      toast.success('Папка переименована');
    } catch { toast.error('Ошибка'); }
  };

  const moveToFolder = async (threadId, folderId) => {
    try {
      await api.put('/staff-chat/folders/move', { thread_id: threadId, folder_id: folderId });
      fetchFolders();
      toast.success(folderId ? 'Перемещено в папку' : 'Убрано из папки');
    } catch { toast.error('Ошибка'); }
  };

  // DnD handlers
  const handleDragStart = (e, threadId) => {
    setDraggingThread(threadId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', threadId);
  };
  const handleDragEnd = () => { setDraggingThread(null); setDragOverFolder(null); };
  const handleDragOver = (e, folderId) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverFolder(folderId); };
  const handleDragLeave = () => setDragOverFolder(null);
  const handleDrop = (e, folderId) => {
    e.preventDefault();
    const threadId = e.dataTransfer.getData('text/plain');
    if (threadId) moveToFolder(threadId, folderId);
    setDragOverFolder(null); setDraggingThread(null);
  };

  // Get threads grouped by folder
  const getThreadFolder = (threadId) => folders.find(f => (f.thread_ids || []).includes(threadId));
  const filteredPatientThreads = threads.filter(t => {
    if (activeFolderFilter === null) return true;
    if (activeFolderFilter === '') return !getThreadFolder(t.id);
    const folder = folders.find(f => f.id === activeFolderFilter);
    return folder && (folder.thread_ids || []).includes(t.id);
  });
  const filteredStaffThreads = staffThreads.filter(t => {
    if (activeFolderFilter === null) return true;
    if (activeFolderFilter === '') return !getThreadFolder(t.id);
    const folder = folders.find(f => f.id === activeFolderFilter);
    return folder && (folder.thread_ids || []).includes(t.id);
  });

  const uploadFile = async (file) => {
    if (!selectedThread || !file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      await api.post(`/chat/threads/${selectedThread.id}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      fetchMessages();
      fetchThreads();
    } catch (err) { toast.error(err.response?.data?.detail || 'Ошибка загрузки файла'); }
  };

  const saveToFiles = async (messageId) => {
    try {
      await api.post(`/chat/messages/${messageId}/save-to-files`);
      toast.success('Файл сохранён в хранилище');
    } catch (err) { toast.error(err.response?.data?.detail || 'Ошибка'); }
  };

  const escalate = async (doctorId) => {
    if (!selectedThread) return;
    try {
      const params = doctorId ? `?doctor_id=${doctorId}` : '';
      await api.post(`/chat/threads/${selectedThread.id}/escalate${params}`);
      toast.success('Чат передан врачу');
      setEscalateDialogOpen(false);
      fetchThreads();
      fetchMessages();
      setSelectedThread(prev => prev ? { ...prev, mode: 'doctor' } : null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка');
    }
  };

  const handleEscalateClick = () => {
    if (isClinic && clinicDoctors.length > 0) {
      // Clinic: show doctor selection dialog
      setEscalateDialogOpen(true);
    } else {
      // Solo practice: escalate directly
      escalate();
    }
  };

  const closeEscalation = async () => {
    if (!selectedThread) return;
    try {
      await api.post(`/chat/threads/${selectedThread.id}/close-escalation`);
      toast.success('Консультация завершена');
      fetchThreads();
      fetchMessages();
      setSelectedThread(prev => prev ? { ...prev, mode: 'admin' } : null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка');
    }
  };

  const toggleHighlight = async (messageId, currentState) => {
    try {
      await api.put(`/chat/messages/${messageId}/highlight?highlighted=${!currentState}`);
      setMessages(prev => prev.map(m => m.id === messageId ? { ...m, highlighted: !currentState } : m));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка');
    }
  };

  const getAiSuggestions = async () => {
    if (!selectedThread) return;
    setLoadingSuggestions(true);
    try {
      const { data } = await api.post(`/chat/threads/${selectedThread.id}/ai-suggest`);
      setSuggestions(data.suggestions || []);
    } catch {
      toast.error('Не удалось получить подсказки');
    }
    setLoadingSuggestions(false);
  };

  const addTemplate = async () => {
    if (!newTemplate.trim()) return;
    try {
      const { data } = await api.post('/chat/templates', { text: newTemplate.trim() });
      setTemplates(prev => [...prev, data]);
      setNewTemplate('');
      toast.success('Шаблон сохранён');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка');
    }
  };

  const deleteTemplate = async (id) => {
    try {
      await api.delete(`/chat/templates/${id}`);
      setTemplates(prev => prev.filter(t => t.id !== id));
    } catch {}
  };

  const isEscalated = selectedThread?.mode === 'doctor';
  const canSend = role === 'doctor' || (role === 'admin' && !isEscalated);

  return (
    <div className="flex min-h-screen" style={{ background: 'var(--app-bg)' }}>
      <Sidebar />
      <main className="flex-1 pt-16 lg:pt-0 flex overflow-hidden" data-testid="messenger-page" style={{ height: '100vh' }}>
        {/* Thread List */}
        <div className={`w-full lg:w-80 border-r border-stone-800/80 flex flex-col shrink-0 ${selectedThread ? 'hidden lg:flex' : 'flex'}`}
          style={{ background: 'var(--app-sidebar)' }}>
          <div className="p-4 border-b border-stone-800/60">
            <h2 className="text-lg font-bold" style={{ color: 'var(--app-text)' }}>Сообщения</h2>
            {/* Tab switcher */}
            <div className="flex gap-1 mt-2 bg-stone-800/30 rounded-lg p-0.5">
              <button onClick={() => { setChatTab('patients'); setSelectedStaffThread(null); }}
                className={`flex-1 py-1.5 rounded-md text-xs font-medium transition-colors ${chatTab === 'patients' ? 'bg-amber-700 text-white' : 'text-stone-500'}`}>
                Клиенты {threads.filter(t => t.show_unread > 0).length > 0 && <span className="ml-1 px-1.5 py-0.5 rounded-full bg-red-500 text-white text-[9px]">{threads.filter(t => t.show_unread > 0).length}</span>}
              </button>
              <button onClick={() => { setChatTab('team'); setSelectedThread(null); }}
                className={`flex-1 py-1.5 rounded-md text-xs font-medium transition-colors ${chatTab === 'team' ? 'bg-amber-700 text-white' : 'text-stone-500'}`}>
                Команда {staffThreads.filter(t => t.unread > 0).length > 0 && <span className="ml-1 px-1.5 py-0.5 rounded-full bg-red-500 text-white text-[9px]">{staffThreads.filter(t => t.unread > 0).length}</span>}
              </button>
            </div>
          </div>

          {/* Folders with Drag & Drop */}
          <div className="px-3 py-2 border-b border-stone-800/40 space-y-1">
            {/* "All" filter */}
            <div className="flex items-center gap-1 flex-wrap">
              <button onClick={() => setActiveFolderFilter(null)}
                className={`text-[10px] px-2 py-1 rounded transition-colors ${activeFolderFilter === null ? 'bg-amber-700/30 text-amber-400' : 'bg-stone-800/30 text-stone-500 hover:text-stone-300'}`}>
                Все
              </button>
              {/* Unfiled drop zone */}
              <button
                onClick={() => setActiveFolderFilter('')}
                onDragOver={e => handleDragOver(e, '')}
                onDragLeave={handleDragLeave}
                onDrop={e => handleDrop(e, '')}
                className={`text-[10px] px-2 py-1 rounded transition-all ${
                  dragOverFolder === '' ? 'bg-sky-800/40 text-sky-300 ring-1 ring-sky-600 scale-105' :
                  activeFolderFilter === '' ? 'bg-amber-700/30 text-amber-400' : 'bg-stone-800/30 text-stone-500 hover:text-stone-300'
                }`}>
                Без папки
              </button>
              {/* Folder drop zones */}
              {folders.map(f => (
                <div key={f.id} className="relative group">
                  {renameFolderId === f.id ? (
                    <input autoFocus value={renameFolderVal} onChange={e => setRenameFolderVal(e.target.value)}
                      onBlur={renameFolder} onKeyDown={e => { if (e.key === 'Enter') renameFolder(); if (e.key === 'Escape') setRenameFolderId(null); }}
                      className="text-[10px] px-2 py-1 rounded bg-stone-800 border border-amber-700 text-stone-200 w-24" />
                  ) : (
                    <button
                      onClick={() => setActiveFolderFilter(f.id)}
                      onDragOver={e => handleDragOver(e, f.id)}
                      onDragLeave={handleDragLeave}
                      onDrop={e => handleDrop(e, f.id)}
                      className={`text-[10px] px-2 py-1 rounded transition-all flex items-center gap-1 ${
                        dragOverFolder === f.id ? 'bg-amber-700/40 text-amber-300 ring-1 ring-amber-600 scale-105' :
                        activeFolderFilter === f.id ? 'bg-amber-700/30 text-amber-400' : 'bg-stone-800/30 text-stone-500 hover:text-stone-300'
                      }`}>
                      {f.name} <span className="text-stone-600">{f.thread_ids?.length || 0}</span>
                      <span className="hidden group-hover:inline-flex items-center gap-0.5 ml-0.5">
                        <span onClick={e => { e.stopPropagation(); setRenameFolderId(f.id); setRenameFolderVal(f.name); }} className="hover:text-amber-400 cursor-pointer"><Pencil size={8} /></span>
                        <span onClick={e => { e.stopPropagation(); deleteFolder(f.id); }} className="hover:text-red-400 cursor-pointer"><X size={8} /></span>
                      </span>
                    </button>
                  )}
                </div>
              ))}
              <button onClick={() => setShowNewFolder(!showNewFolder)}
                className="text-[10px] px-1.5 py-1 text-amber-600 hover:text-amber-400 rounded hover:bg-stone-800/30">
                <Plus size={10} />
              </button>
            </div>
            {showNewFolder && (
              <div className="flex gap-1 mt-1">
                <input value={newFolderName} onChange={e => setNewFolderName(e.target.value)} placeholder="Имя папки"
                  className="flex-1 bg-stone-800 border border-stone-700 rounded px-2 py-1 text-xs text-stone-200"
                  onKeyDown={e => e.key === 'Enter' && createFolder()} autoFocus />
                <button onClick={createFolder} className="text-xs text-amber-500 px-2 hover:text-amber-400">OK</button>
              </div>
            )}
            {draggingThread && (
              <p className="text-[9px] text-stone-600 mt-1">Перетащите чат на папку</p>
            )}
          </div>

          <div className="flex-1 overflow-y-auto">
            {/* Patient threads */}
            {chatTab === 'patients' && (
              filteredPatientThreads.length === 0 ? (
              <div className="text-center py-16 px-4" style={{ color: 'var(--app-text-muted)' }}>
                <MessageCircle size={36} className="mx-auto mb-3 opacity-20" />
                <p className="text-sm">Нет активных чатов</p>
              </div>
            ) : (
              filteredPatientThreads.map(t => {
                const isActive = selectedThread?.id === t.id;
                const isDoc = t.mode === 'doctor';
                const threadFolder = getThreadFolder(t.id);
                return (
                  <button key={t.id} data-testid={`thread-${t.id}`}
                    draggable
                    onDragStart={e => handleDragStart(e, t.id)}
                    onDragEnd={handleDragEnd}
                    onClick={() => { setSelectedThread(t); setSuggestions([]); setShowTemplates(false); }}
                    className={`w-full text-left px-4 py-3 border-b border-stone-800/40 transition-all ${
                      isActive ? 'bg-amber-900/10' : 'hover:bg-stone-800/30'
                    } ${draggingThread === t.id ? 'opacity-50 scale-95' : ''}`}>
                    <div className="flex items-center gap-2">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                        isDoc ? 'bg-violet-900/30' : 'bg-stone-800'
                      }`}>
                        <User size={14} className={isDoc ? 'text-violet-400' : 'text-stone-500'} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm font-medium truncate" style={{ color: 'var(--app-text)' }}>
                            {t.patient_name || 'Пациент'}
                          </span>
                          {isDoc && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-violet-900/40 text-violet-400 font-medium shrink-0">
                              Передано специалисту
                            </span>
                          )}
                          {threadFolder && (
                            <span className="text-[8px] px-1 py-0.5 rounded bg-stone-800 text-stone-600 shrink-0">{threadFolder.name}</span>
                          )}
                        </div>
                        <p className="text-xs truncate" style={{ color: 'var(--app-text-muted)' }}>
                          {t.last_message || 'Нет сообщений'}
                        </p>
                      </div>
                      {(t.show_unread || 0) > 0 && (
                        <span className="w-5 h-5 rounded-full bg-amber-600 text-white text-[10px] font-bold flex items-center justify-center shrink-0">
                          {t.show_unread}
                        </span>
                      )}
                    </div>
                  </button>
                );
              })
            ))}

            {/* Team threads */}
            {chatTab === 'team' && (
              <>
              {/* New chat button */}
              {colleagues.length > 0 && (
                <div className="p-2 border-b border-stone-800/40">
                  <select onChange={e => { if (e.target.value) startStaffChat(e.target.value); e.target.value = ''; }}
                    className="w-full bg-stone-800 border border-stone-700 rounded px-2 py-1.5 text-xs text-stone-300">
                    <option value="">Начать чат с...</option>
                    {colleagues.map(c => <option key={c.id} value={c.id}>{c.name} ({c.role === 'owner' ? 'Владелец' : c.role === 'admin' ? 'Админ' : 'Специалист'})</option>)}
                  </select>
                </div>
              )}
              {filteredStaffThreads.length === 0 ? (
                <div className="text-center py-16 px-4" style={{ color: 'var(--app-text-muted)' }}>
                  <Users size={36} className="mx-auto mb-3 opacity-20" />
                  <p className="text-sm">Нет внутренних чатов</p>
                </div>
              ) : (
                filteredStaffThreads.map(t => {
                  const isActive = selectedStaffThread?.id === t.id;
                  const otherId = t.participants.find(p => p !== myId);
                  const otherName = t.participant_names?.[otherId] || 'Коллега';
                  const otherRole = t.participant_roles?.[otherId] || '';
                  const threadFolder = getThreadFolder(t.id);
                  return (
                    <button key={t.id}
                      draggable
                      onDragStart={e => handleDragStart(e, t.id)}
                      onDragEnd={handleDragEnd}
                      onClick={() => setSelectedStaffThread(t)}
                      className={`w-full text-left px-4 py-3 border-b border-stone-800/30 transition-all ${
                        isActive ? 'bg-stone-800/50' : 'hover:bg-stone-800/20'
                      } ${draggingThread === t.id ? 'opacity-50 scale-95' : ''}`}>
                      <div className="flex items-center justify-between">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium truncate" style={{ color: 'var(--app-text)' }}>{otherName}</span>
                            <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: 'var(--app-card)', color: 'var(--app-text-muted)' }}>
                              {otherRole === 'owner' ? 'Владелец' : otherRole === 'admin' ? 'Админ' : 'Специалист'}
                            </span>
                            {threadFolder && (
                              <span className="text-[8px] px-1 py-0.5 rounded bg-stone-800 text-stone-600 shrink-0">{threadFolder.name}</span>
                            )}
                          </div>
                          <p className="text-xs truncate mt-0.5" style={{ color: 'var(--app-text-muted)' }}>{t.last_message || 'Нет сообщений'}</p>
                        </div>
                        {(t.unread || 0) > 0 && (
                          <span className="w-5 h-5 rounded-full bg-amber-600 text-white text-[10px] font-bold flex items-center justify-center shrink-0">{t.unread}</span>
                        )}
                      </div>
                    </button>
                  );
                })
              )}
              </>
            )}
          </div>
        </div>

        {/* Chat Area */}
        <div className={`flex-1 flex flex-col ${!selectedThread && !selectedStaffThread ? 'hidden lg:flex' : 'flex'}`}>
          {/* Staff chat view */}
          {selectedStaffThread && !selectedThread ? (
            <>
              <div className="px-4 py-3 border-b border-stone-800/60 flex items-center gap-3" style={{ background: 'var(--app-sidebar)' }}>
                <button className="lg:hidden mr-1" onClick={() => setSelectedStaffThread(null)}>
                  <ChevronLeft size={20} style={{ color: 'var(--app-text-muted)' }} />
                </button>
                <User size={18} style={{ color: 'var(--app-accent)' }} />
                <div>
                  <p className="text-sm font-medium" style={{ color: 'var(--app-text)' }}>
                    {selectedStaffThread.participant_names?.[selectedStaffThread.participants.find(p => p !== myId)] || 'Коллега'}
                  </p>
                  <p className="text-[10px]" style={{ color: 'var(--app-text-muted)' }}>Внутренний чат</p>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-2">
                {staffMessages.map(m => (
                  <div key={m.id} className={`flex ${m.sender_id === myId ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[70%] px-3 py-2 rounded-xl text-sm ${m.sender_id === myId ? 'rounded-br-sm' : 'rounded-bl-sm'}`}
                      style={{ background: m.sender_id === myId ? 'var(--app-accent)' : 'var(--app-card)', color: m.sender_id === myId ? '#fff' : 'var(--app-text)' }}>
                      {m.sender_id !== myId && <p className="text-[10px] font-medium opacity-60 mb-0.5">{m.sender_name}</p>}
                      <p className="whitespace-pre-wrap break-words">{m.text}</p>
                      <p className="text-[9px] mt-1 opacity-40">{new Date(m.created_at).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}</p>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
              <div className="p-3 border-t border-stone-800/60" style={{ background: 'var(--app-sidebar)' }}>
                <form onSubmit={e => { e.preventDefault(); sendStaffMessage(); }} className="flex gap-2">
                  <input value={staffText} onChange={e => setStaffText(e.target.value)}
                    placeholder="Сообщение коллеге..." className="flex-1 bg-stone-800/50 border border-stone-700/50 rounded-lg px-3 py-2 text-sm text-stone-200 placeholder:text-stone-600 focus:outline-none focus:border-amber-700/60" />
                  <Button type="submit" size="icon" disabled={!staffText.trim()} className="bg-amber-700 hover:bg-amber-600 text-white shrink-0">
                    <Send size={16} />
                  </Button>
                </form>
              </div>
            </>
          ) : !selectedThread ? (
            <div className="flex-1 flex items-center justify-center" style={{ color: 'var(--app-text-muted)' }}>
              <div className="text-center">
                <MessageCircle size={48} className="mx-auto mb-3 opacity-15" />
                <p className="text-sm">Выберите чат</p>
              </div>
            </div>
          ) : (
            <>
              {/* Chat header */}
              <div className="px-4 py-3 border-b border-stone-800/60 flex items-center justify-between"
                style={{ background: 'var(--app-sidebar)' }}>
                <div className="flex items-center gap-3">
                  <button className="lg:hidden p-1" onClick={() => setSelectedThread(null)}>
                    <ChevronLeft size={20} style={{ color: 'var(--app-text-muted)' }} />
                  </button>
                  <div>
                    <p className="text-sm font-medium" style={{ color: 'var(--app-text)' }}>
                      {selectedThread.patient_name}
                    </p>
                    <p className="text-[10px]" style={{ color: 'var(--app-text-muted)' }}>
                      {isEscalated ? 'Режим: врач-пациент (конфиденциально)' : 'Режим: администратор'}
                      {selectedThread.patient_phone ? ` · ${selectedThread.patient_phone}` : ''}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  {role === 'admin' && !isEscalated && hasDoctors && (
                    <Button data-testid="escalate-btn" size="sm" variant="outline"
                      onClick={handleEscalateClick}
                      className="text-xs gap-1 border-violet-800/50 text-violet-400 hover:bg-violet-900/20">
                      <ArrowUpRight size={14} /> Передать врачу
                    </Button>
                  )}
                  {role === 'admin' && !isEscalated && !hasDoctors && (
                    <span data-testid="no-doctors-hint" className="text-[10px] text-stone-600 self-center">
                      Нет врачей для эскалации
                    </span>
                  )}
                  {role === 'doctor' && isEscalated && (
                    <Button data-testid="close-escalation-btn" size="sm" variant="outline"
                      onClick={closeEscalation}
                      className="text-xs gap-1 border-emerald-800/50 text-emerald-400 hover:bg-emerald-900/20">
                      <XCircle size={14} /> Завершить консультацию
                    </Button>
                  )}
                </div>
              </div>

              {/* Escalation banner for admin */}
              {role === 'admin' && isEscalated && (
                <div className="px-4 py-2 bg-violet-900/20 border-b border-violet-800/30 text-xs text-violet-400 flex items-center gap-2">
                  <Shield size={14} />
                  Чат передан врачу. Вы не видите сообщения в этом режиме и не можете отправлять.
                </div>
              )}

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-2">
                {messages.map(m => (
                  <div key={m.id} className={`flex ${
                    m.sender_type === 'patient' ? 'justify-start' :
                    m.sender_type === 'system' ? 'justify-center' :
                    'justify-end'
                  }`}>
                    <div data-testid={`msg-${m.id}`}
                      className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm relative group ${
                        m.sender_type === 'patient'
                          ? 'bg-stone-800 text-stone-200 rounded-bl-sm'
                          : m.sender_type === 'system'
                          ? 'bg-transparent text-stone-600 text-xs italic'
                          : m.sender_type === 'doctor'
                          ? 'bg-violet-700/80 text-white rounded-br-sm'
                          : 'bg-amber-600 text-white rounded-br-sm'
                      } ${m.highlighted ? 'ring-2 ring-yellow-500/60' : ''}`}>
                      {m.sender_type === 'patient' && (
                        <p className="text-[10px] font-medium text-stone-500 mb-0.5">{m.sender_name}</p>
                      )}
                      {m.sender_type !== 'patient' && m.sender_type !== 'system' && (
                        <p className="text-[10px] font-medium opacity-60 mb-0.5">{m.sender_name || (m.sender_type === 'doctor' ? 'Врач' : 'Администратор')}</p>
                      )}
                      <p className="whitespace-pre-wrap break-words">{m.attachment ? '' : m.text}</p>
                      {/* File attachment */}
                      {m.attachment && (
                        <div className="flex items-center gap-2 mt-1">
                          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/chat/attachments/${m.attachment.filename}`}
                            target="_blank" rel="noopener noreferrer"
                            className="flex items-center gap-1.5 text-xs underline underline-offset-2 opacity-80 hover:opacity-100">
                            <Paperclip size={12} /> {m.attachment.original_name}
                            <span className="opacity-50">({Math.round(m.attachment.size / 1024)}KB)</span>
                          </a>
                          {m.sender_type === 'patient' && (role === 'admin' || role === 'doctor') && (
                            <button onClick={() => saveToFiles(m.id)} title="Сохранить в файлы пациента"
                              className="opacity-0 group-hover:opacity-70 hover:!opacity-100 transition-opacity">
                              <FolderInput size={14} />
                            </button>
                          )}
                        </div>
                      )}
                      {/* Highlight indicator for doctor */}
                      {m.highlighted && role === 'doctor' && (
                        <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-yellow-500 flex items-center justify-center">
                          <Star size={10} className="text-stone-900" fill="currentColor" />
                        </span>
                      )}
                      {/* Highlight toggle for admin (on patient messages) */}
                      {role === 'admin' && m.sender_type === 'patient' && !isEscalated && (
                        <button data-testid={`highlight-${m.id}`}
                          onClick={() => toggleHighlight(m.id, !!m.highlighted)}
                          className={`absolute -top-2 -right-2 w-5 h-5 rounded-full flex items-center justify-center transition-all ${
                            m.highlighted
                              ? 'bg-yellow-500 text-stone-900 opacity-100'
                              : 'bg-stone-700 text-stone-400 opacity-0 group-hover:opacity-100'
                          }`}
                          title={m.highlighted ? 'Убрать подсветку' : 'Подсветить для врача'}>
                          <Star size={10} fill={m.highlighted ? 'currentColor' : 'none'} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* AI Suggestions */}
              {suggestions.length > 0 && (
                <div className="px-4 py-2 border-t border-stone-800/40 flex gap-2 overflow-x-auto">
                  {suggestions.map((s, i) => (
                    <button key={i} data-testid={`ai-suggestion-${i}`}
                      onClick={() => { setText(s); setSuggestions([]); inputRef.current?.focus(); }}
                      className="shrink-0 text-xs px-3 py-1.5 rounded-full bg-amber-900/20 text-amber-400 border border-amber-800/30 hover:bg-amber-900/40 transition-colors">
                      {s.length > 50 ? s.slice(0, 50) + '...' : s}
                    </button>
                  ))}
                </div>
              )}

              {/* Quick Reply Templates panel */}
              {showTemplates && canSend && (
                <div data-testid="templates-panel" className="px-4 py-3 border-t border-stone-800/40 space-y-2" style={{ background: 'var(--app-sidebar)', maxHeight: '220px', overflowY: 'auto' }}>
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs font-medium" style={{ color: 'var(--app-text-muted)' }}>Шаблоны быстрых ответов</p>
                    <button data-testid="close-templates-btn" onClick={() => setShowTemplates(false)} className="text-stone-600 hover:text-stone-400">
                      <X size={14} />
                    </button>
                  </div>
                  {templates.length === 0 ? (
                    <p className="text-xs text-stone-600 py-2">Нет шаблонов. Создайте первый ниже.</p>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {templates.map(t => (
                        <div key={t.id} className="group flex items-center gap-1">
                          <button data-testid={`template-use-${t.id}`}
                            onClick={() => { setText(t.text); setShowTemplates(false); inputRef.current?.focus(); }}
                            className="text-xs px-3 py-1.5 rounded-full bg-stone-800 text-stone-300 border border-stone-700/60 hover:bg-stone-700 hover:text-stone-100 transition-colors">
                            {t.text.length > 45 ? t.text.slice(0, 45) + '...' : t.text}
                          </button>
                          <button data-testid={`template-delete-${t.id}`}
                            onClick={() => deleteTemplate(t.id)}
                            className="opacity-0 group-hover:opacity-100 text-stone-600 hover:text-red-400 transition-opacity p-0.5">
                            <Trash2 size={11} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2 pt-1">
                    <input data-testid="new-template-input" value={newTemplate} onChange={e => setNewTemplate(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && addTemplate()}
                      placeholder="Новый шаблон ответа..."
                      className="flex-1 bg-stone-800 border border-stone-700 rounded-full px-3 py-1.5 text-xs text-stone-200 placeholder:text-stone-600 focus:outline-none focus:border-stone-600"
                    />
                    <button data-testid="add-template-btn" onClick={addTemplate} disabled={!newTemplate.trim()}
                      className="w-7 h-7 rounded-full bg-stone-700 hover:bg-stone-600 disabled:opacity-30 text-stone-300 flex items-center justify-center shrink-0 transition-colors">
                      <Plus size={14} />
                    </button>
                  </div>
                </div>
              )}

              {/* Input */}
              {canSend && (
                <div className="p-3 border-t border-stone-800/60" style={{ background: 'var(--app-sidebar)' }}>
                  <form onSubmit={e => { e.preventDefault(); sendMessage(); }} className="flex gap-2">
                    <Button data-testid="templates-toggle-btn" type="button" size="icon" variant="ghost"
                      onClick={() => setShowTemplates(v => !v)}
                      className={`shrink-0 hover:bg-stone-800/50 ${showTemplates ? 'text-amber-400' : 'text-stone-500/50 hover:text-stone-400'}`}>
                      <Bookmark size={18} />
                    </Button>
                    <Button data-testid="ai-suggest-btn" type="button" size="icon" variant="ghost"
                      onClick={getAiSuggestions} disabled={loadingSuggestions}
                      className="shrink-0 text-amber-500/50 hover:text-amber-400 hover:bg-amber-900/20">
                      <Sparkles size={18} className={loadingSuggestions ? 'animate-spin' : ''} />
                    </Button>
                    <label className="shrink-0 cursor-pointer text-stone-500/50 hover:text-stone-400 flex items-center justify-center w-9 h-9 rounded-full hover:bg-stone-800/50 transition-colors">
                      <Paperclip size={18} />
                      <input type="file" className="hidden" onChange={e => { if (e.target.files?.[0]) uploadFile(e.target.files[0]); e.target.value = ''; }} />
                    </label>
                    <input ref={inputRef} data-testid="staff-chat-input"
                      value={text} onChange={e => setText(e.target.value)}
                      placeholder="Написать ответ..."
                      className="flex-1 bg-stone-800 border border-stone-700 rounded-full px-4 py-2 text-sm text-stone-200 placeholder:text-stone-600 focus:outline-none focus:border-amber-700/60"
                    />
                    <button data-testid="staff-chat-send" type="submit" disabled={!text.trim() || sending}
                      className="w-9 h-9 rounded-full bg-amber-600 hover:bg-amber-500 disabled:opacity-30 text-white flex items-center justify-center shrink-0 transition-colors">
                      <Send size={16} />
                    </button>
                  </form>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* Doctor selection dialog for clinic escalation */}
      <Dialog open={escalateDialogOpen} onOpenChange={setEscalateDialogOpen}>
        <DialogContent className="bg-stone-900 border-stone-800 text-stone-200 max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-stone-200">Передать врачу</DialogTitle>
          </DialogHeader>
          <p className="text-stone-500 text-xs mb-3">Выберите врача, который будет вести переписку с пациентом</p>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {clinicDoctors.map(doc => (
              <button key={doc.id} data-testid={`escalate-doctor-${doc.id}`}
                onClick={() => escalate(doc.id)}
                className="w-full flex items-center gap-3 p-3 rounded-lg border border-stone-800 bg-stone-950/30 hover:border-violet-700/50 hover:bg-violet-900/10 transition-colors text-left">
                <div className="w-9 h-9 rounded-full bg-violet-900/30 flex items-center justify-center shrink-0">
                  <Stethoscope size={16} className="text-violet-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-stone-200 truncate">{doc.name}</p>
                  {doc.specialization && <p className="text-[10px] text-stone-500 truncate">{doc.specialization}</p>}
                </div>
                <ArrowUpRight size={14} className="text-stone-600 shrink-0" />
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
